import React from 'react';
import styled from 'styled-components';
import { Wallet, PieChart } from 'lucide-react';
import { GeneralExpenseSummaryDto, formatCurrency } from '../../services/generalExpensesService';

interface ExpensesSummaryCardsProps {
  summary: GeneralExpenseSummaryDto | null;
  loading?: boolean;
}

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(240px, 1fr) 2fr;
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  padding: 20px;
  box-shadow: var(--shadow-light);
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 12px;
`;

const TotalAmount = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: var(--primary-color);
`;

const CountHint = styled.div`
  margin-top: 6px;
  font-size: 13px;
  color: var(--text-tertiary);
`;

const BreakdownList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const BreakdownRow = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  padding: 6px 0;
  border-bottom: 1px solid var(--border-color-light);

  &:last-child {
    border-bottom: none;
  }
`;

const CategoryName = styled.span`
  color: var(--text-primary);
`;

const CategoryTotal = styled.span`
  color: var(--text-secondary);
  font-weight: 500;
`;

const EmptyText = styled.div`
  color: var(--text-tertiary);
  font-size: 14px;
  padding: 8px 0;
`;

const ExpensesSummaryCards: React.FC<ExpensesSummaryCardsProps> = ({ summary, loading }) => {
  const count = summary?.count ?? 0;

  return (
    <Grid>
      <Card>
        <CardHeader>
          <Wallet size={18} />
          סך הוצאות מסוננות
        </CardHeader>
        <TotalAmount>{loading ? '...' : formatCurrency(summary?.total ?? 0)}</TotalAmount>
        {!loading && <CountHint>{`מתוך ${count.toLocaleString('he-IL')} הוצאות`}</CountHint>}
      </Card>

      <Card>
        <CardHeader>
          <PieChart size={18} />
          פירוט לפי קטגוריה
        </CardHeader>
        {loading ? (
          <EmptyText>טוען...</EmptyText>
        ) : summary && summary.byCategory.length > 0 ? (
          <BreakdownList>
            {summary.byCategory.map(c => (
              <BreakdownRow key={c.categoryId}>
                <CategoryName>{c.categoryName}</CategoryName>
                <CategoryTotal>{formatCurrency(c.total)}</CategoryTotal>
              </BreakdownRow>
            ))}
          </BreakdownList>
        ) : (
          <EmptyText>אין הוצאות תואמות לסינון</EmptyText>
        )}
      </Card>
    </Grid>
  );
};

export default ExpensesSummaryCards;
